import React from 'react';
import { Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { CodeAgentBrowserToolsSettings } from '../../types/code-agent';
import { codeAgentFieldSx } from './codeAgentFieldStyles';

// Mirrors aibe DEFAULT_CODE_AGENT_BROWSER_TOOLS; only used until the server returns its own config.
export const DEFAULT_BROWSER_TOOLS: CodeAgentBrowserToolsSettings = {
  enabled: true,
  blockedOrigins: [],
  maxActionsPerBatch: 10,
  maxScreenshotsPerTurn: 8,
  screenshotMaxWidth: 1280,
  consoleBufferSize: 500,
  evalEnabled: false,
  networkBodyEnabled: false,
};

type NumericKey =
  | 'maxActionsPerBatch'
  | 'maxScreenshotsPerTurn'
  | 'screenshotMaxWidth'
  | 'consoleBufferSize';

const NUMERIC_FIELDS: { key: NumericKey; label: string }[] = [
  { key: 'maxActionsPerBatch', label: 'Max actions per browser_act' },
  { key: 'maxScreenshotsPerTurn', label: 'Max screenshots per turn' },
  { key: 'screenshotMaxWidth', label: 'Screenshot max width (px)' },
  { key: 'consoleBufferSize', label: 'Console buffer size' },
];

interface Props {
  browserTools: CodeAgentBrowserToolsSettings;
  onChange: (value: CodeAgentBrowserToolsSettings) => void;
}

export function CodeAgentBrowserToolsSection({ browserTools, onChange }: Props) {
  return (
    <div>
      <Typography className="mb-1 font-semibold text-gray-900 dark:text-slate-100">
        Browser debug tools (used when CODE_AGENT_BROWSER_TOOLS is on)
      </Typography>
      <Typography variant="body2" className="mb-3 text-gray-500 dark:text-slate-400">
        browser_* tools drive the user's own Chrome through the AppAI extension. Blocked origins are
        enforced by the server and again by the CLI before and after every command. Each screenshot
        is analysed by a vision model and charged to the turn.
      </Typography>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {NUMERIC_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            size="small"
            label={label}
            type="number"
            value={browserTools[key]}
            onChange={(e) =>
              onChange({
                ...browserTools,
                [key]: parseInt(e.target.value, 10) || DEFAULT_BROWSER_TOOLS[key],
              })
            }
            sx={codeAgentFieldSx}
            inputProps={{ min: 1 }}
          />
        ))}
        <TextField
          size="small"
          label="Blocked origins (one host per line, *.host allowed)"
          multiline
          minRows={2}
          className="sm:col-span-2"
          value={browserTools.blockedOrigins.join('\n')}
          onChange={(e) =>
            onChange({
              ...browserTools,
              blockedOrigins: e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
            })
          }
          sx={codeAgentFieldSx}
        />
      </div>
      <div className="mt-2 flex flex-col">
        <FormControlLabel
          control={
            <Checkbox
              checked={browserTools.enabled}
              onChange={(e) => onChange({ ...browserTools, enabled: e.target.checked })}
            />
          }
          label="Browser tools enabled (kill switch)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={browserTools.evalEnabled}
              onChange={(e) => onChange({ ...browserTools, evalEnabled: e.target.checked })}
            />
          }
          label="Allow browser_eval (runs arbitrary JavaScript in the page; cookie/storage block is best-effort)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={browserTools.networkBodyEnabled}
              onChange={(e) => onChange({ ...browserTools, networkBodyEnabled: e.target.checked })}
            />
          }
          label="Allow response bodies in browser_network"
        />
      </div>
    </div>
  );
}
